import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../config/firebase";
import { useTasks } from "../context/TaskContext";
import Chat from "../components/Chat";
import {
  extractStudyMaterial,
  sendCoachMessage,
} from "../services/studyCoachApi";
import { fetchCalendarEvents } from "../services/googleCalendar";
import {
  addBasicNotesToAnki,
  checkAnkiConnection,
} from "../services/ankiConnect";
import "./StudyCoach.css";

const MAX_STORED_MATERIAL_TEXT = 80000;

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());

const normalizeTaskSuggestion = (task, index) => {
  const title = String(task?.title || task?.task || "").trim();
  if (!title) return null;
  const focusMinutes = Number(task?.focusMinutes);
  const breakMinutes = Number(task?.breakMinutes);
  const cycles = Number(task?.cycles);
  return {
    id: String(task?.id || `task-${index + 1}`),
    title,
    dateId: isIsoDate(task?.dateId) ? String(task.dateId) : null,
    focusMinutes: Number.isFinite(focusMinutes)
      ? Math.max(5, Math.min(180, Math.round(focusMinutes)))
      : 25,
    breakMinutes: Number.isFinite(breakMinutes)
      ? Math.max(0, Math.min(90, Math.round(breakMinutes)))
      : 5,
    cycles: Number.isFinite(cycles)
      ? Math.max(1, Math.min(10, Math.round(cycles)))
      : 1,
    rationale: String(task?.rationale || "").trim(),
  };
};

const normalizeCardSuggestion = (card, index) => {
  const front = String(card?.front || "").trim();
  const back = String(card?.back || "").trim();
  if (!front || !back) return null;
  const tags = Array.isArray(card?.tags)
    ? card.tags.map((tag) => String(tag || "").trim()).filter(Boolean)
    : [];
  return {
    id: String(card?.id || `card-${index + 1}`),
    front,
    back,
    deckName: String(card?.deckName || "Obey The Bell").trim() || "Obey The Bell",
    tags,
  };
};

const formatCalendarTime = (isoString, allDay = false) => {
  if (!isoString) return "Unknown";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  if (allDay) {
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const starterMessage = {
  role: "assistant",
  content:
    "I can help you plan your study day. Upload class materials, sync Google Calendar, then tell me what exams or topics to prioritize.",
};

const StudyCoach = () => {
  const {
    user,
    selectedDate,
    formatDateId,
    ensureListForDate,
    setSelectedDate,
    addTask,
    createFlashcard,
  } = useTasks();

  const fileInputRef = useRef(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [ankiBusy, setAnkiBusy] = useState(false);
  const [error, setError] = useState("");
  const [conversation, setConversation] = useState([starterMessage]);
  const [coachGoal, setCoachGoal] = useState("");
  const [coachConstraints, setCoachConstraints] = useState("");
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarSyncedAt, setCalendarSyncedAt] = useState(null);
  const [ankiVersion, setAnkiVersion] = useState(null);
  const [suggestedTasks, setSuggestedTasks] = useState([]);
  const [suggestedFlashcards, setSuggestedFlashcards] = useState([]);

  useEffect(() => {
    if (!user?.uid) {
      setMaterials([]);
      setSelectedMaterialIds([]);
      return undefined;
    }

    const materialsRef = collection(db, "users", user.uid, "studyCoachMaterials");
    const materialsQuery = query(
      materialsRef,
      orderBy("uploadedAt", "desc"),
      limit(40)
    );

    const unsubscribe = onSnapshot(
      materialsQuery,
      (snapshot) => {
        const nextMaterials = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaterials(nextMaterials);
        setSelectedMaterialIds((prev) =>
          prev.filter((id) => nextMaterials.some((material) => material.id === id))
        );
      },
      (snapshotError) => {
        console.error("Failed to load study materials", snapshotError);
        setError(snapshotError.message || "Failed to load study materials.");
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  useEffect(() => {
    if (!materials.length || selectedMaterialIds.length) return;
    setSelectedMaterialIds(materials.slice(0, 3).map((material) => material.id));
  }, [materials, selectedMaterialIds.length]);

  const selectedMaterials = useMemo(() => {
    if (!selectedMaterialIds.length) return [];
    return materials.filter((material) => selectedMaterialIds.includes(material.id));
  }, [materials, selectedMaterialIds]);

  const handleSelectMaterial = (materialId) => {
    setSelectedMaterialIds((prev) =>
      prev.includes(materialId)
        ? prev.filter((id) => id !== materialId)
        : [...prev, materialId]
    );
  };

  const handleUploadMaterial = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file || !user?.uid) return;
      setUploadBusy(true);
      setError("");
      try {
        const extraction = await extractStudyMaterial(file);
        const storagePath = `users/${user.uid}/studyCoach/materials/${Date.now()}_${
          file.name || "material"
        }`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const storageUrl = await getDownloadURL(storageRef);

        const textContent = String(extraction?.extractedText || "")
          .trim()
          .slice(0, MAX_STORED_MATERIAL_TEXT);
        if (!textContent) {
          throw new Error("No readable text extracted from the file.");
        }

        const materialRef = await addDoc(
          collection(db, "users", user.uid, "studyCoachMaterials"),
          {
            name: String(file.name || extraction?.name || "Material"),
            mimeType: String(file.type || extraction?.mimeType || "application/octet-stream"),
            size: Number(file.size || extraction?.size || 0),
            uploadedAt: Date.now(),
            storageUrl,
            storagePath,
            textContent,
            preview: textContent.slice(0, 500),
          }
        );

        setSelectedMaterialIds((prev) => Array.from(new Set([materialRef.id, ...prev])));
      } catch (uploadError) {
        console.error("Failed to upload study material", uploadError);
        setError(uploadError.message || "Failed to upload material.");
      } finally {
        setUploadBusy(false);
        event.target.value = "";
      }
    },
    [user?.uid]
  );

  const handleSyncCalendar = useCallback(async () => {
    setCalendarBusy(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("https://www.googleapis.com/auth/calendar.readonly");
      provider.setCustomParameters({
        prompt: "consent",
        include_granted_scopes: "true",
      });

      const result = await signInWithPopup(auth, provider);
      if (user?.uid && result?.user?.uid !== user.uid) {
        throw new Error("Please choose the same account you use in this app.");
      }

      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;
      if (!accessToken) {
        throw new Error("Could not access Google Calendar token.");
      }

      const events = await fetchCalendarEvents(accessToken, {
        daysAhead: 21,
        perCalendarLimit: 40,
      });
      setCalendarEvents(events);
      setCalendarSyncedAt(Date.now());
    } catch (calendarError) {
      console.error("Calendar sync failed", calendarError);
      setError(calendarError.message || "Failed to sync Google Calendar.");
    } finally {
      setCalendarBusy(false);
    }
  }, [user?.uid]);

  const buildCoachContext = useCallback(() => {
    const now = new Date();
    const defaultDate = selectedDate || formatDateId(now);
    return {
      todayDateId: defaultDate,
      userGoal: coachGoal.trim(),
      constraints: coachConstraints.trim(),
      calendarEvents: calendarEvents.slice(0, 120),
      materials: selectedMaterials.slice(0, 6).map((material) => ({
        id: material.id,
        name: material.name,
        excerpt: String(material.textContent || "").slice(0, 8000),
      })),
    };
  }, [calendarEvents, coachConstraints, coachGoal, formatDateId, selectedDate, selectedMaterials]);

  const handleCoachMessage = useCallback(
    async (message) => {
      const trimmed = String(message || "").trim();
      if (!trimmed) return;
      setChatBusy(true);
      setError("");
      const priorConversation = [...conversation];
      setConversation((prev) => [...prev, { role: "user", content: trimmed }]);

      try {
        const result = await sendCoachMessage({
          conversation: priorConversation,
          message: trimmed,
          context: buildCoachContext(),
        });
        const assistantMessage = String(result?.assistantMessage || "").trim();
        setConversation((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              assistantMessage ||
              "I can continue once you share your goals, constraints, or class materials.",
          },
        ]);

        const tasks = Array.isArray(result?.suggestions?.tasks)
          ? result.suggestions.tasks
              .map((task, index) => normalizeTaskSuggestion(task, index))
              .filter(Boolean)
          : [];
        const flashcards = Array.isArray(result?.suggestions?.flashcards)
          ? result.suggestions.flashcards
              .map((card, index) => normalizeCardSuggestion(card, index))
              .filter(Boolean)
          : [];
        setSuggestedTasks(tasks);
        setSuggestedFlashcards(flashcards);
      } catch (chatError) {
        console.error("Coach chat failed", chatError);
        setError(chatError.message || "Coach request failed.");
      } finally {
        setChatBusy(false);
      }
    },
    [buildCoachContext, conversation]
  );

  const applySuggestedTasks = useCallback(async () => {
    if (!suggestedTasks.length) return;
    const confirmed = window.confirm(
      `Add ${suggestedTasks.length} suggested task${
        suggestedTasks.length === 1 ? "" : "s"
      } to your plan?`
    );
    if (!confirmed) return;

    setApplyBusy(true);
    setError("");
    const previousDate = selectedDate || formatDateId(new Date());
    try {
      for (const task of suggestedTasks) {
        const targetDateId = isIsoDate(task.dateId)
          ? task.dateId
          : previousDate;
        // eslint-disable-next-line no-await-in-loop
        await ensureListForDate(targetDateId);
        // eslint-disable-next-line no-await-in-loop
        await addTask({
          task: task.title,
          primaryDuration: task.focusMinutes * 60,
          secondaryDuration: task.breakMinutes * 60,
          numCycles: task.cycles,
          tilDone: false,
          isProblemSolving: false,
        });
      }
      await Promise.resolve(setSelectedDate(previousDate, { ensureList: true }));
      setConversation((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Added ${suggestedTasks.length} study task${
            suggestedTasks.length === 1 ? "" : "s"
          } to your lists.`,
        },
      ]);
    } catch (applyError) {
      console.error("Failed to apply suggested tasks", applyError);
      setError(applyError.message || "Failed to apply suggested tasks.");
    } finally {
      setApplyBusy(false);
    }
  }, [addTask, ensureListForDate, formatDateId, selectedDate, setSelectedDate, suggestedTasks]);

  const applySuggestedFlashcards = useCallback(async () => {
    if (!suggestedFlashcards.length) return;
    const confirmed = window.confirm(
      `Create ${suggestedFlashcards.length} flashcard${
        suggestedFlashcards.length === 1 ? "" : "s"
      } in your library?`
    );
    if (!confirmed) return;

    setApplyBusy(true);
    setError("");
    try {
      for (const card of suggestedFlashcards) {
        // eslint-disable-next-line no-await-in-loop
        await createFlashcard({
          front: card.front,
          back: card.back,
          tags: card.tags,
          deckName: card.deckName,
          noteType: "Basic",
          origin: "study-coach",
          type: "basic",
        });
      }
      setConversation((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Saved ${suggestedFlashcards.length} flashcard${
            suggestedFlashcards.length === 1 ? "" : "s"
          } to your library.`,
        },
      ]);
    } catch (applyError) {
      console.error("Failed to apply suggested flashcards", applyError);
      setError(applyError.message || "Failed to save flashcards.");
    } finally {
      setApplyBusy(false);
    }
  }, [createFlashcard, suggestedFlashcards]);

  const handleCheckAnki = useCallback(async () => {
    setAnkiBusy(true);
    setError("");
    try {
      const version = await checkAnkiConnection();
      setAnkiVersion(version);
    } catch (ankiError) {
      console.error("AnkiConnect check failed", ankiError);
      setError(ankiError.message || "Could not connect to AnkiConnect.");
      setAnkiVersion(null);
    } finally {
      setAnkiBusy(false);
    }
  }, []);

  const handleSendToAnki = useCallback(async () => {
    if (!suggestedFlashcards.length) return;
    const confirmed = window.confirm(
      `Send ${suggestedFlashcards.length} suggested flashcard${
        suggestedFlashcards.length === 1 ? "" : "s"
      } to AnkiConnect now?`
    );
    if (!confirmed) return;
    setAnkiBusy(true);
    setError("");
    try {
      await addBasicNotesToAnki(suggestedFlashcards);
      setConversation((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Sent ${suggestedFlashcards.length} flashcard${
            suggestedFlashcards.length === 1 ? "" : "s"
          } to Anki.`,
        },
      ]);
    } catch (ankiError) {
      console.error("Failed to send notes to Anki", ankiError);
      setError(ankiError.message || "Failed to send notes to Anki.");
    } finally {
      setAnkiBusy(false);
    }
  }, [suggestedFlashcards]);

  return (
    <div className="app-layout">
      <header className="page-header">
        <div className="page-header__content">
          <h1>Study coach</h1>
          <p>
            Build a daily study plan with AI, your uploaded materials, and your
            Google Calendar commitments.
          </p>
        </div>
        <div className="page-header__aside">
          <span className="badge">{materials.length} materials</span>
          <span className="badge">{calendarEvents.length} calendar events</span>
        </div>
      </header>

      {error ? (
        <section className="page-section page-section--subtle">
          <p className="mini-label" style={{ color: "#fecaca" }}>
            {error}
          </p>
        </section>
      ) : null}

      <div className="workspace-grid">
        <section className="page-section workspace-main">
          <div className="page-section__headline">
            <div>
              <h2>Coach chat</h2>
              <p className="section-caption" style={{ marginBottom: 0 }}>
                Ask for a plan, request practice sets, or generate flashcards from
                selected materials.
              </p>
            </div>
          </div>

          <div className="study-coach-context-grid">
            <div className="task-form__field">
              <label htmlFor="coach-goal">Current goal</label>
              <textarea
                id="coach-goal"
                rows={3}
                placeholder="Example: Midterm in 10 days, focus on chapter 5 and chapter 6."
                value={coachGoal}
                onChange={(event) => setCoachGoal(event.target.value)}
              />
            </div>
            <div className="task-form__field">
              <label htmlFor="coach-constraints">Constraints</label>
              <textarea
                id="coach-constraints"
                rows={3}
                placeholder="Example: Work 3pm-8pm Mon/Wed/Fri. Need a light plan on Thursdays."
                value={coachConstraints}
                onChange={(event) => setCoachConstraints(event.target.value)}
              />
            </div>
          </div>

          <Chat
            conversation={conversation}
            onSendMessage={handleCoachMessage}
            busy={chatBusy}
            placeholder="Ask the coach to plan your next study sessions..."
            disabled={chatBusy || applyBusy || uploadBusy}
            disabledPlaceholder="Coach is busy right now..."
          />
        </section>

        <aside className="workspace-aside">
          <section className="page-section page-section--condensed">
            <div className="page-section__headline">
              <h2>Materials</h2>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadBusy}
              >
                {uploadBusy ? "Uploading..." : "Upload PDF/TXT"}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,text/plain,application/pdf"
              style={{ display: "none" }}
              onChange={handleUploadMaterial}
            />
            <div className="study-coach-material-list">
              {materials.length ? (
                materials.map((material) => {
                  const selected = selectedMaterialIds.includes(material.id);
                  return (
                    <button
                      key={material.id}
                      type="button"
                      className={`study-coach-material ${
                        selected ? "study-coach-material--active" : ""
                      }`}
                      onClick={() => handleSelectMaterial(material.id)}
                    >
                      <strong>{material.name || "Material"}</strong>
                      <span>{String(material.preview || "").slice(0, 120) || "No preview"}</span>
                    </button>
                  );
                })
              ) : (
                <p className="mini-label">Upload class materials to ground the coach.</p>
              )}
            </div>
          </section>

          <section className="page-section page-section--condensed">
            <div className="page-section__headline">
              <h2>Calendar</h2>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={handleSyncCalendar}
                disabled={calendarBusy}
              >
                {calendarBusy ? "Syncing..." : "Sync Google"}
              </button>
            </div>
            <p className="mini-label" style={{ marginTop: 0 }}>
              {calendarSyncedAt
                ? `Last synced ${new Date(calendarSyncedAt).toLocaleString()}`
                : "Pulls the next 21 days from your Google calendars."}
            </p>
            <div className="study-coach-event-list">
              {calendarEvents.length ? (
                calendarEvents.slice(0, 10).map((event) => (
                  <div key={`${event.calendarId}-${event.id}`} className="study-coach-event">
                    <strong>{event.summary}</strong>
                    <span>{formatCalendarTime(event.start, event.allDay)}</span>
                  </div>
                ))
              ) : (
                <p className="mini-label">No synced events yet.</p>
              )}
            </div>
          </section>

          <section className="page-section page-section--condensed">
            <div className="page-section__headline">
              <h2>Suggestions</h2>
              <span className="badge">Confirm before apply</span>
            </div>

            <div className="study-coach-suggestion-block">
              <div className="cluster" style={{ justifyContent: "space-between" }}>
                <strong>Tasks</strong>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => void applySuggestedTasks()}
                  disabled={!suggestedTasks.length || applyBusy}
                >
                  Apply tasks
                </button>
              </div>
              <div className="study-coach-suggestion-list">
                {suggestedTasks.length ? (
                  suggestedTasks.map((task) => (
                    <div key={task.id} className="study-coach-suggestion-item">
                      <strong>{task.title}</strong>
                      <span>
                        {task.dateId || selectedDate || formatDateId(new Date())} · {task.focusMinutes}m focus · {task.cycles} cycle
                        {task.cycles === 1 ? "" : "s"}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="mini-label">No task suggestions yet.</p>
                )}
              </div>
            </div>

            <div className="study-coach-suggestion-block">
              <div className="cluster" style={{ justifyContent: "space-between" }}>
                <strong>Flashcards</strong>
                <div className="cluster">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void applySuggestedFlashcards()}
                    disabled={!suggestedFlashcards.length || applyBusy}
                  >
                    Save cards
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void handleSendToAnki()}
                    disabled={!suggestedFlashcards.length || ankiBusy}
                  >
                    Send to Anki
                  </button>
                </div>
              </div>
              <div className="study-coach-suggestion-list">
                {suggestedFlashcards.length ? (
                  suggestedFlashcards.map((card) => (
                    <div key={card.id} className="study-coach-suggestion-item">
                      <strong>{card.front}</strong>
                      <span>{card.deckName}</span>
                    </div>
                  ))
                ) : (
                  <p className="mini-label">No flashcard suggestions yet.</p>
                )}
              </div>
            </div>

            <div className="cluster" style={{ justifyContent: "space-between" }}>
              <span className="mini-label">
                {ankiVersion ? `AnkiConnect version ${ankiVersion}` : "Anki status unknown"}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => void handleCheckAnki()}
                disabled={ankiBusy}
              >
                {ankiBusy ? "Checking..." : "Check Anki"}
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default StudyCoach;
